import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Search } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { serverMessage } from '~/lib/api/server-error';

export interface LinkColumn<T> {
	header: string;
	value: (row: T) => string;
	className?: string;
}

interface Props<T> {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	subtitle?: string;
	rows: T[];
	loading: boolean;
	getKey: (row: T) => number | string;
	isLinked: (row: T) => boolean;
	onToggle: (row: T, next: boolean) => Promise<unknown>;
	toggling: boolean;
	columns: LinkColumn<T>[];
	/** Free-text search over a row. */
	searchText: (row: T) => string;
	emptyText: string;
}

/**
 * Per-master link sub-view: a candidate list with a per-row link toggle (parity with the
 * legacy SalesPersonLinked{Inspectors,Customers} popup grids). Toggling calls `onToggle`,
 * which links/unlinks server-side and refetches.
 */
export function LinkToggleDialog<T>({
	open,
	onOpenChange,
	title,
	subtitle,
	rows,
	loading,
	getKey,
	isLinked,
	onToggle,
	toggling,
	columns,
	searchText,
	emptyText
}: Props<T>) {
	const { t } = useTranslation();
	const [search, setSearch] = useState('');

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return q ? rows.filter(r => searchText(r).toLowerCase().includes(q)) : rows;
	}, [rows, search, searchText]);

	const toggle = async (row: T, next: boolean) => {
		try {
			await onToggle(row, next);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					{subtitle && <DialogDescription>{subtitle}</DialogDescription>}
				</DialogHeader>

				<div className="relative">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('common.search')} className="pl-8" />
				</div>

				<div className="max-h-[55vh] overflow-y-auto rounded-md border border-border">
					{loading ? (
						<div className="flex flex-col gap-2 p-3">
							{Array.from({ length: 6 }).map((_, i) => (
								<Skeleton key={i} className="h-9 w-full rounded-md" />
							))}
						</div>
					) : filtered.length === 0 ? (
						<p className="py-12 text-center text-sm text-foreground-muted">{emptyText}</p>
					) : (
						<table className="w-full border-collapse text-sm">
							<thead className="sticky top-0 bg-elevated text-xs text-foreground-muted">
								<tr className="border-b border-border">
									{columns.map((c, i) => (
										<th key={i} className="px-3 py-2 text-left font-medium">
											{c.header}
										</th>
									))}
									<th className="px-3 py-2 text-center font-medium" style={{ width: 90 }}>
										{t('customers.links.linked')}
									</th>
								</tr>
							</thead>
							<tbody>
								{filtered.map(row => (
									<tr key={getKey(row)} className="border-b border-border/60 last:border-b-0 hover:bg-muted/50">
										{columns.map((c, i) => (
											<td key={i} className={c.className ?? 'px-3 py-2 text-foreground-muted'}>
												{c.value(row) || '—'}
											</td>
										))}
										<td className="px-3 py-2 text-center">
											<Switch checked={isLinked(row)} disabled={toggling} onCheckedChange={next => void toggle(row, next)} />
										</td>
									</tr>
								))}
							</tbody>
						</table>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
